import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Shared "one open at a time" accordion pieces.
 *
 * - `useExclusiveOpen()`: keeps track of which single item is open. Opening one closes the others.
 * - `Collapse`: height animation using the CSS grid 0fr/1fr trick. It opens and closes at the same
 *   moment as its siblings, so the content below slides smoothly with no jump.
 * - `Accordion`: ready-made FAQ style list built on the two above.
 * - `ExpandableText`: clamped paragraph (bio, quote) that grows and shrinks smoothly.
 *
 * Collapsed content stays in the DOM (readable by search engines) but is not focusable.
 */

const EASE = "duration-300 ease-in-out motion-reduce:transition-none";

export function useExclusiveOpen(initial = null) {
  const [openKey, setOpenKey] = useState(initial);
  const toggle = useCallback((key) => setOpenKey((current) => (current === key ? null : key)), []);
  return [openKey, toggle];
}

export function Collapse({ open, id, labelledBy, children }) {
  return (
    <div
      id={id}
      role="region"
      aria-labelledby={labelledBy}
      className={`grid transition-[grid-template-rows,opacity,visibility] ${EASE} ${
        open ? "visible grid-rows-[1fr] opacity-100" : "invisible grid-rows-[0fr] opacity-0"
      }`}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

/** items: [{ id, title, content }]. `as` sets the heading level that wraps each trigger button. */
export function Accordion({ items, as: Heading = "h3", testId, className = "" }) {
  const [openKey, toggle] = useExclusiveOpen(null);
  const baseId = useId();

  return (
    <ul className={`space-y-3 ${className}`} data-testid={testId}>
      {items.map((item, index) => {
        const open = openKey === item.id;
        const buttonId = `${baseId}-btn-${index}`;
        const panelId = `${baseId}-panel-${index}`;
        return (
          <li key={item.id}>
            <article
              className={`rounded-3xl border bg-white transition-colors ${EASE} ${
                open ? "border-brand-500" : "border-brand-200 hover:border-brand-400"
              }`}
            >
              <Heading>
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => toggle(item.id)}
                  data-testid={testId ? `${testId}-item-${index}` : undefined}
                  className="flex min-h-[44px] w-full items-center justify-between gap-4 rounded-3xl p-5 text-left text-base font-semibold"
                >
                  <span>{item.title}</span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-brand-500 transition-transform ${EASE} ${open ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </button>
              </Heading>
              <Collapse open={open} id={panelId} labelledBy={buttonId}>
                <div className="px-5 pb-5 text-base leading-relaxed text-ink/70">{item.content}</div>
              </Collapse>
            </article>
          </li>
        );
      })}
    </ul>
  );
}

function measure(node, lines) {
  const style = window.getComputedStyle(node);
  const fontSize = parseFloat(style.fontSize) || 16;
  const lineHeight = parseFloat(style.lineHeight) || fontSize * 1.4;
  return { full: node.scrollHeight, clamp: Math.round(lineHeight * lines) };
}

/**
 * Paragraph clamped to `lines` lines that animates to full height when `open`.
 * Short text that already fits is left alone. Reports whether a toggle is needed via `onOverflowChange`.
 */
export function ExpandableText({ id, open, lines = 4, className = "", fadeFrom = "from-white", onOverflowChange, children }) {
  const ref = useRef(null);
  const [sizes, setSizes] = useState(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = () => {
      const next = measure(node, lines);
      setSizes((prev) => (prev && prev.full === next.full && prev.clamp === next.clamp ? prev : next));
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [lines, children]);

  const overflowing = sizes ? sizes.full > sizes.clamp + 2 : false;

  useLayoutEffect(() => {
    if (onOverflowChange) onOverflowChange(overflowing);
  }, [overflowing, onOverflowChange]);

  const maxHeight = sizes && overflowing ? (open ? sizes.full : sizes.clamp) : undefined;

  return (
    <div className="relative">
      <p
        id={id}
        ref={ref}
        style={maxHeight === undefined ? undefined : { maxHeight }}
        className={`overflow-hidden transition-[max-height] ${EASE} ${className}`}
      >
        {children}
      </p>
      {overflowing ? (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t ${fadeFrom} to-transparent transition-opacity ${EASE} ${
            open ? "opacity-0" : "opacity-100"
          }`}
        />
      ) : null}
    </div>
  );
}
